import { useCallback, useEffect, useRef, useState } from "react";
import { FaArrowLeft } from "react-icons/fa6";
import { Link, useSearchParams } from "react-router-dom";
import { Masonry } from "./components/everything/Masonry";
import { Preview } from "./components/everything/Preview";
import { NoteForm } from "./components/everything/NoteForm";
import { SearchBar } from "./components/everything/SearchBar";
import { UploadZone, MAX_BYTES, probeDimensions } from "./components/everything/UploadZone";
import { getFileUrl } from "./lib/telegram";
import {
  loadManifest,
  addFile,
  reorder,
  removeFile,
  getStorageUsage,
  STORAGE_LIMIT_BYTES,
} from "./lib/manifest";
import { downloadFile } from "./lib/fileFetch";
import { cacheUpload } from "./lib/fileCache";
import { uploadFileToB2 } from "./lib/b2Upload";
import { formatBytes } from "./components/everything/FileTile";
import { DeleteAuth } from "./components/everything/DeleteAuth";
import { ManifestQuota } from "./components/everything/ManifestQuota";
import { ev, evMsg } from "./lib/everythingCopy";
import { AuroraBackground } from "./components/AuroraBackground";
import { FlashbangIcon } from "./components/everything/FlashbangIcon";
import { useAppTheme } from "./hooks/useAppTheme";

// Files this size or smaller get their bytes cached in IndexedDB after upload
// so previews are instant. Bigger files just stream from B2 on demand.
const CACHE_BLOB_MAX_BYTES = 25 * 1024 * 1024;

function clearFileParam(searchParams, setSearchParams) {
  if (!searchParams.get("file")) return;
  const next = new URLSearchParams(searchParams);
  next.delete("file");
  setSearchParams(next, { replace: true });
}

function newId() {
  return `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function Everything() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [opened, setOpened] = useState(null);
  const [mediaUrls, setMediaUrls] = useState({});
  const [noteTitle, setNoteTitle] = useState("");
  const [noteText, setNoteText] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Bumps 3 seconds after the user stops typing in the search box. The Masonry
  // listens to it to re-trigger the "fire border" celebration animation on
  // matching tiles — telling the user "your match is here" once they pause.
  const [searchFlashTick, setSearchFlashTick] = useState(0);
  const inputRef = useRef(null);
  const { theme, toggle: toggleTheme } = useAppTheme();

  useEffect(() => {
    if (!searchQuery.trim()) return;
    const id = setTimeout(() => setSearchFlashTick((t) => t + 1), 3000);
    return () => clearTimeout(id);
  }, [searchQuery]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "guidoti's File Transfer";
    return () => { document.title = previousTitle; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadManifest()
      .then((f) => { if (!cancelled) { setFiles(f); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const needed = files.filter(
      (f) =>
        (f.mime?.startsWith("image/") ||
          f.mime?.startsWith("video/") ||
          f.mime?.startsWith("audio/")) &&
        !mediaUrls[f.id]
    );
    if (needed.length === 0) return;
    const next = {};
    for (const f of needed) {
      // getFileUrl is storage-aware: returns /api/tg/file for Telegram-backed
      // files, /api/b2/file for B2-backed ones. No async needed — the URL is
      // deterministic.
      next[f.id] = getFileUrl(f);
    }
    setMediaUrls((prev) => ({ ...prev, ...next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const showToast = useCallback((msg, kind = "error") => {
    // Queue toasts so simultaneous events (e.g. batch upload finishing 5 files
    // at once) each get their own timer — otherwise later setTimeouts clobber
    // earlier toasts and the user only ever sees the last one.
    const id = `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    setToasts((curr) => [...curr, { id, msg, kind }]);
    setTimeout(() => {
      setToasts((curr) => curr.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const openFile = useCallback((file) => {
    setOpened(file);
    setSearchParams({ file: file.id }, { replace: false });
  }, [setSearchParams]);

  const closePreview = useCallback(() => {
    setOpened(null);
    clearFileParam(searchParams, setSearchParams);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (loading) return;
    const fileId = searchParams.get("file");
    if (!fileId) {
      setOpened(null);
      return;
    }
    const match = files.find((f) => f.id === fileId);
    if (match) {
      setOpened((prev) => (prev?.id === match.id ? prev : match));
      return;
    }
    setOpened(null);
    clearFileParam(searchParams, setSearchParams);
    if (files.length > 0) {
      showToast(ev.fileNotFound, "error");
    }
  }, [loading, files, searchParams, setSearchParams, showToast]);

  const handleFiles = useCallback(async (incoming) => {
    for (const file of incoming) {
      if (file.size > MAX_BYTES) {
        showToast(evMsg(ev.overLimit, { name: file.name, size: formatBytes(file.size) }));
        continue;
      }
      // Refuse uploads that would push the bucket over the free-tier limit,
      // recomputed against the latest manifest (including any uploads currently
      // in flight in this loop iteration via the local `files` snapshot).
      const usage = getStorageUsage(files);
      if (usage.used + file.size > STORAGE_LIMIT_BYTES) {
        showToast(
          evMsg(ev.quotaExceeded, {
            name: file.name,
            remaining: formatBytes(STORAGE_LIMIT_BYTES - usage.used),
          })
        );
        continue;
      }
      const tempId = newId();
      setUploads((u) => [
        ...u,
        { id: tempId, name: file.name, size: file.size, mime: file.type, progress: 0 },
      ]);
      const reportProgress = (pct) => {
        setUploads((u) =>
          u.map((up) => (up.id === tempId ? { ...up, progress: pct } : up))
        );
      };
      try {
        const dims = await probeDimensions(file);
        const entry = await uploadFileToB2({
          id: tempId,
          file,
          dims,
          onProgress: reportProgress,
        });
        if (file.size <= CACHE_BLOB_MAX_BYTES) {
          // Stash the blob locally for snappy previews while the user is in
          // this session. Skipped for big files (the cache would balloon).
          await cacheUpload(tempId, file);
        }
        const next = await addFile(entry);
        setFiles(next);
        showToast(evMsg(ev.uploadSuccess, { name: file.name }), "success");
      } catch (e) {
        showToast(evMsg(ev.uploadFailed, { name: file.name, error: e.message }));
      } finally {
        setUploads((u) => u.filter((up) => up.id !== tempId));
      }
    }
  }, [files, showToast]);

  const handleDownload = useCallback(async (file) => {
    try {
      const mode = await downloadFile(file, () => getFileUrl(file));
      if (mode === "opened-tab") {
        showToast(evMsg(ev.downloadOpened, { name: file.name }), "success");
      } else {
        showToast(evMsg(ev.downloadSuccess, { name: file.name }), "success");
      }
    } catch (e) {
      showToast(evMsg(ev.downloadFailed, { error: e.message }));
    }
  }, [showToast]);

  const handleSaveNote = useCallback(async () => {
    const text = noteText.trim();
    if (!text) {
      showToast(ev.noteEmpty);
      return;
    }
    setNoteSaving(true);
    try {
      const title =
        noteTitle.trim() ||
        `note-${new Date().toISOString().slice(0, 16).replace(/[T:]/g, "-")}`;
      const filename = title.endsWith(".txt") ? title : `${title}.txt`;
      // Notes live inline in the manifest — no separate blob upload needed.
      const entry = {
        id: newId(),
        kind: "note",
        storage: "note-inline",
        name: filename,
        mime: "text/plain",
        size: new TextEncoder().encode(text).length,
        content: text,
        uploadedAt: new Date().toISOString(),
      };
      const next = await addFile(entry);
      setFiles(next);
      showToast(evMsg(ev.noteSaved, { name: filename }), "success");
      setNoteTitle("");
      setNoteText("");
    } catch (e) {
      showToast(evMsg(ev.noteFailed, { error: e.message }));
    } finally {
      setNoteSaving(false);
    }
  }, [noteText, noteTitle, showToast]);

  const handleDelete = useCallback(async (file) => {
    if (!canDelete) return;
    try {
      const next = await removeFile(file.id);
      setFiles(next);
      setOpened((prev) => {
        if (prev?.id === file.id) {
          clearFileParam(searchParams, setSearchParams);
          return null;
        }
        return prev;
      });
      showToast(evMsg(ev.deleteSuccess, { name: file.name }), "success");
    } catch (e) {
      showToast(evMsg(ev.deleteFailed, { error: e.message }));
    }
  }, [canDelete, searchParams, setSearchParams, showToast]);

  const handleAuth = useCallback((result) => {
    if (result === "ok") {
      setCanDelete(true);
      showToast(ev.authSuccess, "success");
    } else if (result === "fail") {
      showToast(ev.authFailed);
    } else {
      showToast(ev.authNotConfigured);
    }
  }, [showToast]);

  const handleReorder = useCallback(async (next) => {
    setFiles(next);
    try {
      await reorder(next.map((f) => f.id));
    } catch (e) {
      showToast(evMsg(ev.reorderFailed, { error: e.message }));
    }
  }, [showToast]);

  const isDark = theme === "dark";

  return (
    <div className="relative flex flex-col min-h-screen">
      <AuroraBackground theme={theme} />
      <header className="sticky top-0 z-30 bg-white/70 dark:bg-zinc-950/70 backdrop-blur-md border-b border-zinc-200/60 dark:border-zinc-800/60">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <Link
              to="/"
              className="inline-flex items-center justify-center w-8 h-8 rounded-full text-zinc-400 dark:text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all"
              aria-label={ev.backHome}
            >
              <FaArrowLeft className="w-3.5 h-3.5" />
            </Link>
            <h1 className="text-xs sm:text-sm md:text-base font-black tracking-tight text-zinc-900 dark:text-zinc-100 leading-snug min-w-0">
              {ev.title}
            </h1>
            <button
              type="button"
              onClick={toggleTheme}
              className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all shrink-0"
              aria-label={isDark ? ev.themeSwitchToLight : ev.themeSwitchToDark}
              title={isDark ? ev.themeSwitchToLight : ev.themeSwitchToDark}
            >
              <FlashbangIcon className="w-5 h-5" />
            </button>
            {!loading && (
              <span className="text-xs text-zinc-400 dark:text-zinc-500 hidden sm:inline">
                {files.length} {files.length === 1 ? ev.items : ev.itemsPlural}
              </span>
            )}
          </div>
          {!loading && !error && (
            <DeleteAuth authenticated={canDelete} onAuthenticated={handleAuth} />
          )}
          {!loading && !error && <ManifestQuota files={files} />}
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 md:px-8 py-6">
        {loading && <LoadingState message={ev.loadingManifest} />}
        {error && (
          <ErrorState
            error={error}
            retryLabel={ev.retry}
            onRetry={() => {
              setError(null); setLoading(true);
              loadManifest().then((f) => { setFiles(f); setLoading(false); })
                .catch((e) => { setError(e.message); setLoading(false); });
            }}
          />
        )}
        {!loading && !error && (
          <>
            <div className="flex flex-wrap items-start gap-x-8 gap-y-2">
              <NoteForm
                title={noteTitle}
                text={noteText}
                saving={noteSaving}
                onTitleChange={setNoteTitle}
                onTextChange={setNoteText}
                onSave={handleSaveNote}
              />
              <SearchBar value={searchQuery} onChange={setSearchQuery} />
            </div>

            <Masonry
              files={files}
              uploads={uploads}
              mediaUrls={mediaUrls}
              canDelete={canDelete}
              searchQuery={searchQuery}
              searchFlashTick={searchFlashTick}
              onPick={() => inputRef.current?.click()}
              onOpen={openFile}
              onDownload={handleDownload}
              onDelete={handleDelete}
              onReorder={handleReorder}
            />
          </>
        )}
      </main>

      <UploadZone onFiles={handleFiles} inputRef={inputRef} />
      {opened && <Preview file={opened} onClose={closePreview} />}
      {toasts.length > 0 && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[60] flex flex-col items-center gap-2 max-w-sm w-[calc(100%-2rem)] pointer-events-none">
          {toasts.map((t) => (
            <Toast key={t.id} kind={t.kind} msg={t.msg} />
          ))}
        </div>
      )}
    </div>
  );
}

function LoadingState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-32 text-zinc-400 dark:text-zinc-500">
      <span className="w-8 h-8 rounded-full border-2 border-zinc-200 dark:border-zinc-700 border-t-zinc-600 dark:border-t-zinc-300 animate-spin" />
      <p className="mt-4 text-sm">{message}</p>
    </div>
  );
}

function Toast({ kind, msg }) {
  const isSuccess = kind === "success";
  const dot = isSuccess ? "bg-emerald-500" : "bg-rose-500";
  const ring = isSuccess
    ? "ring-emerald-200/60 dark:ring-emerald-700/50"
    : "ring-rose-200/60 dark:ring-rose-800/50";
  return (
    <div
      className={`pointer-events-auto inline-flex items-center gap-2.5 px-3.5 py-2 rounded-full bg-white/95 dark:bg-zinc-900/95 backdrop-blur-md shadow-md ring-1 ${ring} text-[13px] text-zinc-700 dark:text-zinc-200 animate-toast-in`}
      role={isSuccess ? "status" : "alert"}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dot} shrink-0`} />
      <span className="leading-snug">{msg}</span>
    </div>
  );
}

function ErrorState({ error, onRetry, retryLabel }) {
  return (
    <div className="flex flex-col items-center justify-center py-32 text-center">
      <p className="text-sm text-zinc-700 dark:text-zinc-200 font-medium">{error}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 px-4 py-2 rounded-full bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-sm hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors"
      >
        {retryLabel}
      </button>
    </div>
  );
}
